<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script>
/**
 * App.vue - 应用根组件
 * 
 * 本组件是整个应用的核心容器，负责：
 * 1. 提供应用布局框架
 * 2. 初始化 localStorage 数据恢复
 * 3. 监听 storage 事件实现跨标签页数据同步
 * 4. 监听路由变化并同步到其他标签页
 * 5. 监听页面滚动事件并同步滚动位置到其他标签页
 * 
 * 同步原理：
 * - 当用户在一个标签页修改数据时，通过 Vuex action 将数据写入 localStorage
 * - localStorage 的修改会触发其他标签页的 storage 事件
 * - 监听 storage 事件，将变化同步到 Vuex store，实现视图更新
 */
export default {
  name: 'App',
  /**
   * 计算属性
   */
  computed: {
    /**
     * 获取当前标签页唯一标识（用于区分不同标签页实例）
     * @returns {string} 标签页ID
     */
    // tabId () {
    //   return this.$store.getters.tabId;
    // },

    /**
     * 获取当前滚动位置（从 Vuex store）
     * @returns {Object} 滚动位置对象 { x, y }
     */
    scrollPosition () {
      return this.$store.getters.scrollPosition;
    }
  },
  /**
   * 组件挂载钩子
   * 在此初始化同步机制
   */
  mounted () {
    // 1. 从 localStorage 恢复状态（应用启动时调用）
    this.$store.dispatch('initFromLocalStorage');
    // 2. 添加 storage 事件监听（监听其他标签页的 localStorage 变化）
    window.addEventListener('storage', this.handleStorageChange);

    // 3. 监听路由变化，同步路由到其他标签页
    // 路由切换时清除计数器、文本、数字、表单和滚动位置数据
    this.$router.afterEach((to) => {
      // 清除其他数据存储键（保留路由）
      this.$store.dispatch('clearAllData');
      // 更新路由
      this.$store.dispatch('updateRoute', to.path);
      // 重置页面滚动到顶部
      window.scrollTo(0, 0);
    });

    // 4. 添加页面滚动事件监听（实现跨标签页滚动同步）
    window.addEventListener('scroll', this.handleScroll);

    // 控制台输出启动信息
    console.log(
      '%c欢迎使用智能投顾机器人 \n开启你的智能投资之旅吧！',
      'color: #3b82f6; font-size: 14px; font-weight: bold;'
    );
  },
  /**
  * 组件销毁钩子
  * 在此清理事件监听
  */
  beforeDestroy () {
    // 移除 storage 事件监听，避免内存泄漏
    window.removeEventListener('storage', this.handleStorageChange);

    // 移除滚动事件监听，避免内存泄漏
    window.removeEventListener('scroll', this.handleScroll);
  },
  /**
   * 方法定义
   */
  methods: {
    /**
     * 处理 localStorage 变化事件（来自其他标签页）
     * @param {StorageEvent} event - storage 事件对象
     */
    handleStorageChange (event) {
      const { key, newValue } = event;

      // 将变化同步到 Vuex store
      this.$store.dispatch('syncFromStorage', { key, newValue });

      // 如果是路由变化，跳转到对应路由
      if (key === 'sync_master_route' && newValue !== null) {
        const currentPath = this.$route.path;
        if (currentPath !== newValue) {
          this.$router.push(newValue);
        }
      }

      // 如果是滚动位置变化，同步滚动到对应位置
      if (key === 'sync_master_scroll' && newValue !== null) {
        try {
          const scrollData = JSON.parse(newValue);
          // 检查是否需要滚动（避免自身滚动触发的循环）
          const currentX = window.scrollX || window.pageXOffset;
          const currentY = window.scrollY || window.pageYOffset;
          if (Math.abs(currentX - scrollData.x) > 5 || Math.abs(currentY - scrollData.y) > 5) {
            window.scrollTo(scrollData.x, scrollData.y);
          }
        } catch (e) {
          console.error('Failed to parse scroll data:', e);
        }
      }
    },

    /**
     * 处理页面滚动事件
     * 将滚动位置同步到 localStorage，实现跨标签页滚动同步
     */
    handleScroll () {
      // 获取当前滚动位置
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;

      // 获取 store 中保存的滚动位置
      const storedPosition = this.scrollPosition;

      // 只有当滚动位置变化超过阈值时才更新（避免频繁更新）
      if (Math.abs(scrollX - storedPosition.x) > 5 || Math.abs(scrollY - storedPosition.y) > 5) {
        this.$store.dispatch('updateScrollPosition', { x: scrollX, y: scrollY });
      }
    }
  }
}
</script>

<style lang="less">
@import url("App.less");

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Arial', sans-serif;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8f0ff 50%, #f5f0ff 100%);
}

#app {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}
</style>